import { createItem, getItems, FIG_LISTS, createFIGVaultFolder, uploadToFIGVault } from '../api/figApi';
import { getNextInvoiceNumber } from './invoiceNumber';

const MAX_NUMBER_RETRIES = 3;

/**
 * Claim an invoice number with conflict detection.
 * Fetches the next number, checks for existing record, retries up to 3 times.
 */
export async function claimInvoiceNumber(getToken) {
  for (let attempt = 0; attempt < MAX_NUMBER_RETRIES; attempt++) {
    const invoiceNumber = await getNextInvoiceNumber(getToken);
    const token = await getToken();
    const existing = await getItems(token, FIG_LISTS.INVOICES);
    const conflict = existing.some(item => item.Title === invoiceNumber);
    if (!conflict) return invoiceNumber;
    console.warn(`[InvoiceStorage] Number ${invoiceNumber} already exists, retrying (${attempt + 1}/${MAX_NUMBER_RETRIES})`);
  }
  throw new Error('Failed to claim a unique invoice number after retries');
}

/**
 * Write invoice record to FIG_Invoices list.
 * Blocking — must succeed before download.
 *
 * @throws on failure (caller keeps modal open)
 */
export async function writeInvoiceRecord(getToken, invoiceData) {
  const token = await getToken();
  return createItem(token, FIG_LISTS.INVOICES, {
    Title: invoiceData.invoiceNumber,
    InvoiceDate: new Date().toISOString(),
    ClientId: Number(invoiceData.client.id),
    ClientName: invoiceData.client.name,
    Job: invoiceData.job || '',
    ReferenceNumber: invoiceData.referenceNumber || '',
    GrandTotal: invoiceData.grandTotal,
    PoliciesIncluded: JSON.stringify(invoiceData.lineItems.map(li => li.policyId)),
    Status: 'Generated',
    GeneratedDate: invoiceData.generatedAt,
    DisplayMode: invoiceData.displayMode || 'bundled',
  });
}

/**
 * Upload invoice PDF to FIGVault. Best-effort — failure doesn't block close.
 * Creates Clients/{ClientName}/Invoices/ folder if needed.
 *
 * @returns {string|null} vault path on success, null on failure
 */
export async function uploadInvoiceToVault(getToken, invoiceData, pdfBytes, filename) {
  try {
    const token = await getToken();
    const clientName = invoiceData.client.name || 'Unknown';
    const folderPath = `Clients/${clientName}/Invoices`;

    // Create nested folder structure
    const parts = folderPath.split('/');
    let buildPath = '';
    for (const part of parts) {
      buildPath = buildPath ? `${buildPath}/${part}` : part;
      try {
        await createFIGVaultFolder(token, buildPath);
      } catch { /* may already exist */ }
    }

    const vaultPath = `${folderPath}/${filename}`;
    await uploadToFIGVault(token, vaultPath, pdfBytes);
    return vaultPath;
  } catch (err) {
    console.error('[InvoiceStorage] FIGVault upload failed:', err.message);
    return null;
  }
}
